import { useControlledState } from "@react-stately/utils";
import { Phone } from "@untitledui/icons";
import { HintText } from "@/components/base/input/hint-text";
import type { InputBaseProps } from "@/components/base/input/input";
import { InputBase, TextField } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { formatPhoneNumber, parsePhoneValue } from "@/utils/phone";
export { formatPhoneNumber, parsePhoneValue };

export interface PhoneInputProps extends InputBaseProps {
  /** Whether to show the phone icon */
  showIcon?: boolean;
}

export const PhoneInput = ({
  onChange,
  value,
  defaultValue,
  className,
  maxLength = 14, // (XXX) XXX-XXXX = 14 chars
  label,
  hint,
  showIcon = false,
  ...props
}: PhoneInputProps) => {
  const [displayValue, setDisplayValue] = useControlledState(
    value ? formatPhoneNumber(value) : "",
    defaultValue ? formatPhoneNumber(defaultValue) : "",
    (newDisplayValue) => {
      const numericValue = parsePhoneValue(newDisplayValue || "");
      onChange?.(numericValue);
    },
  );

  const handleChange = (newValue: string) => {
    // Allow empty value
    if (newValue === "") {
      setDisplayValue("");
      return;
    }

    // Format the input value
    const formatted = formatPhoneNumber(newValue);
    setDisplayValue(formatted);
  };

  return (
    <TextField
      aria-label={!label ? props?.placeholder : undefined}
      {...props}
      className={className}
      maxLength={maxLength}
      value={displayValue}
      onChange={handleChange}
      type="tel"
      inputMode="tel"
    >
      {({ isDisabled, isInvalid, isRequired }) => (
        <>
          {label && <Label isRequired={isRequired}>{label}</Label>}

          <InputBase
            {...props}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            icon={showIcon ? Phone : undefined}
          />

          {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
        </>
      )}
    </TextField>
  );
};

PhoneInput.displayName = "PhoneInput";
